import { Input } from '@/components/design-system/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/design-system/popover';
import { useSearchBar } from '@/features/header/hooks/useSearchBar';
import { cn } from '@/utils/cn';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { SearchResults } from './SearchResults';

interface SearchBarProps {
  onSearch?: (query: string) => void;
}

export const SearchBar: React.FC<SearchBarProps> = ({ onSearch }) => {
  const { t } = useTranslation();
  const {
    query,
    open,
    isLoading,
    showResults,
    inputRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleResultSelect,
    setIsMouseOverPopover,
    setOpen
  } = useSearchBar({ onSearch });

  const handlePopoverMouseEnter = (): void => {
    setIsMouseOverPopover(true);
  };
  const handlePopoverMouseLeave = (): void => {
    setIsMouseOverPopover(false);

    if (document.activeElement !== inputRef.current) {
      setOpen(false);
    }
  };

  const handleOpenChange = (newOpen: boolean): void => {
    if (!newOpen && document.activeElement === inputRef.current) {
      return;
    }
    setOpen(newOpen);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            size="lg"
            placeholder={t('label.search')}
            value={query}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            className={cn(
              'peer font-sm w-full rounded-lg border py-2 pr-12 pl-3 transition-colors duration-100',
              'border-border-100 bg-background-900/85 placeholder:text-typography-200',
              'focus:border-border hover:border-border-100 hover:bg-white focus:bg-white focus-visible:ring-0 md:text-base'
            )}
          />
          <span
            className={cn(
              'border-border-100 absolute top-0 right-3 flex h-full items-center justify-center border-l pl-3 transition-colors duration-200',
              'peer-focus:border-primary peer-focus:[&_svg]:text-primary',
              '[&_svg]:transition-colors [&_svg]:duration-200',
              isLoading && 'opacity-50'
            )}
          >
            <SearchOutlined className={cn('text-typography-200', isLoading && 'animate-spin')} />
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn('mt-3 w-100 border-0 p-0', !showResults && 'hidden')}
        align="center"
        sideOffset={8}
        onMouseEnter={handlePopoverMouseEnter}
        onMouseLeave={handlePopoverMouseLeave}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <SearchResults query={query} onSelect={handleResultSelect} />
      </PopoverContent>
    </Popover>
  );
};
